import { showRootHeaderAtom } from '@hakimo-ui/hakimo/util';
import { useSetAtom } from 'jotai';
import { createContext, ReactNode, useContext, useEffect } from 'react';

interface PageLayoutProps {
  variant?: 'split' | 'single';
  title: string;
  icon?: React.ElementType;
  titleAction?: ReactNode;
  secondaryAction?: ReactNode;
  listCollapsed?: boolean;
  children: ReactNode;
}

interface PageLayoutListProps {
  children: ReactNode;
}

interface PageLayoutContextValue {
  title: string;
  icon?: React.ElementType;
  variant: 'split' | 'single';
  titleAction?: ReactNode;
  secondaryAction?: ReactNode;
  listCollapsed: boolean;
}

const LIST_WIDTH = '24rem';
const LIST_MARGIN = '0.125rem';

// Horizontal space a PageLayout.List occupies, for callers that align to it.
export const PAGE_LAYOUT_LIST_WIDTH = `calc(${LIST_WIDTH} + ${LIST_MARGIN})`;

const PageLayoutContext = createContext<PageLayoutContextValue | null>(null);

function usePageLayoutContext() {
  const context = useContext(PageLayoutContext);
  if (!context) {
    throw new Error(
      'PageLayout compound components must be used within PageLayout'
    );
  }
  return context;
}

export function usePageLayoutVariant() {
  return useContext(PageLayoutContext)?.variant;
}

function PageLayoutRoot({
  variant = 'split',
  title,
  icon,
  titleAction,
  secondaryAction,
  listCollapsed = false,
  children,
}: PageLayoutProps) {
  const setShowRootHeader = useSetAtom(showRootHeaderAtom);

  useEffect(() => {
    setShowRootHeader(false);
  }, [setShowRootHeader]);

  return (
    <PageLayoutContext.Provider
      value={{
        title,
        icon,
        variant,
        titleAction,
        secondaryAction,
        listCollapsed,
      }}
    >
      <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg flex h-full w-full overflow-hidden">
        {children}
      </div>
    </PageLayoutContext.Provider>
  );
}

function PageLayoutList({ children }: PageLayoutListProps) {
  const {
    title,
    icon: Icon,
    titleAction,
    secondaryAction,
    listCollapsed,
  } = usePageLayoutContext();

  return (
    <div
      style={{
        width: LIST_WIDTH,
        minWidth: LIST_WIDTH,
        marginLeft: listCollapsed ? `calc(-1 * ${LIST_WIDTH})` : LIST_MARGIN,
      }}
      className="bg-onlight-bg-1 dark:bg-ondark-bg-1 relative z-10 flex flex-col overflow-hidden transition-[margin] duration-300 ease-in-out"
    >
      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 h-16 flex-none px-4 py-3">
        <div className="flex items-center gap-2">
          {Icon && (
            <Icon className="dark:text-dark-text h-6 w-6 text-gray-800" />
          )}
          <span className="dark:text-dark-text text-lg font-semibold text-gray-900">
            {title}
          </span>
          {titleAction && <div>{titleAction}</div>}
          {secondaryAction && <div className="ml-auto">{secondaryAction}</div>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">{children}</div>
    </div>
  );
}

PageLayoutList.displayName = 'PageLayoutList';

function PageLayoutDetails({ children }: { children: ReactNode }) {
  return (
    <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg flex min-w-0 flex-1">
      <div className="ml-0.5 flex-1 overflow-y-auto">
        <div className="mx-auto h-full w-full">{children}</div>
      </div>
    </div>
  );
}

PageLayoutDetails.displayName = 'PageLayoutDetails';

function PageLayoutContent({ children }: { children: ReactNode }) {
  return (
    <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg flex min-w-0 flex-1">
      <div className="ml-0.5 flex-1 overflow-y-auto">
        <div className="mx-auto flex h-full w-full flex-col">{children}</div>
      </div>
    </div>
  );
}

PageLayoutContent.displayName = 'PageLayoutContent';

export const PageLayout = Object.assign(PageLayoutRoot, {
  List: PageLayoutList,
  Details: PageLayoutDetails,
  Content: PageLayoutContent,
});

export default PageLayout;
